import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './ChoferModal';
import ChoferTabs from './ChoferTabs';
// 👇 mismos íconos que en Días Festivos
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Choferes";

const endpoint = ENDPOINTS.chofer;
const endpointById = ENDPOINTS.choferById;
const modelo = Models.chofer;

// 🔗 Endpoints extras para enriquecer chofer con nombre de Persona
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointCorreo = ENDPOINTS.correos;
const modeloCorreo = Models.correos;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    numeroLicencia: f.numeroLicencia,
    claseLicencia: f.claseLicencia,
    venceLicencia: f.venceLicencia || null, // YYYY-MM-DD
    observaciones: f.observaciones,
    activo: !!f.activo,
    recibeNotificaciones: f.recibeNotificaciones ?? true,
  }),
  mapUpdate: (id, f) => ({
    idChofer: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    numeroLicencia: f.numeroLicencia,
    claseLicencia: f.claseLicencia,
    venceLicencia: f.venceLicencia || null,
    observaciones: f.observaciones,
    activo: !!f.activo,
    recibeNotificaciones: f.recibeNotificaciones ?? true,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Choferes');

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 🔥 Igual que en el ejemplo: cargamos choferes + empleados + personas + correos
      const [resChoferes, resEmpleados, resPersonas, resCorreos] =
        await Promise.all([
          axios.get(endpoint),
          axios.get(endpointEmpleado),
          axios.get(endpointPersona),
          axios.get(endpointCorreo),
        ]);

      let choferesNorm = (resChoferes.data || []).map((x) => modelo(x));
      let empleadosNorm = (resEmpleados.data || []).map((x) => modeloEmpleado(x));
      let personasNorm = (resPersonas.data || []).map((x) => modeloPersona(x));
      let correosNorm = (resCorreos.data || []).map((x) => modeloCorreo(x));

      // Enriquecer chofer con:
      // - nombreChofer: "Nombre ApPaterno ApMaterno"
      // - numeroEmpleado
      // - correoChofer
      choferesNorm = choferesNorm.map((ch) => {
        const emp = empleadosNorm.find(e => e.idEmpleado === ch.idEmpleado);
        const per = emp
          ? personasNorm.find(p => p.idPersona === emp.idPersona)
          : null;
        const cor = per
          ? correosNorm.find(c => c.idPersona === per.idPersona)
          : null;

        const fullName = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
          : (ch.nombre || '');

        return {
          ...ch,
          nombreChofer: fullName,
          numeroEmpleado: emp?.numeroEmpleado ?? '',
          correoChofer: cor?.correo || '',
        };
      });

      setRows(choferesNorm);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    const sessionEmpresaId = getSessionEmpresaId();
    const isSuperAdmin = isSessionSuperAdmin();

    let source = rows;
    if (!isSuperAdmin) {
      source = rows.filter(r => r.idEmpresa === sessionEmpresaId || !r.idEmpresa);
    }

    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter((r) => {
      const lic = (r.numeroLicencia || '').toLowerCase();
      const clase = (r.claseLicencia || '').toLowerCase();
      const vence = (r.venceLicencia || '').toLowerCase();
      const obs = (r.observaciones || '').toLowerCase();
      const empId = String(r.idEmpleado ?? '').toLowerCase();
      const empNo = String(r.numeroEmpleado ?? '').toLowerCase();
      const nom = (r.nombreChofer || '').toLowerCase();
      const empName = (r.nombreEmpresa || '').toLowerCase();

      return (
        lic.includes(q) ||
        clase.includes(q) ||
        vence.includes(q) ||
        obs.includes(q) ||
        empId.includes(q) ||
        empNo.includes(q) ||
        nom.includes(q) ||
        empName.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idChofer === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idChofer ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotificaciones = async (chofer) => {
    const nuevoEstado = !chofer.recibeNotificaciones;
    const payload = maps.mapUpdate(chofer.idChofer, {
      ...chofer,
      recibeNotificaciones: nuevoEstado
    });

    try {
      setLoading(true);
      await axios.put(endpoint, payload);
      await loadData();
    } catch (e) {
      console.error('Error al cambiar estado de notificaciones:', e);
      alert('No se pudo cambiar el estado de las notificaciones.');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre, licencia, clase, vencimiento, observaciones o empleado…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Chofer</th>
                  <th>No. Empleado</th>
                  <th>No. Licencia</th>
                  <th>Clase</th>
                  <th>Vence</th>
                  <th>Observaciones</th>
                  <th style={{ textAlign: 'center' }}>Notificaciones</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idChofer}>
                    <td>{normalizeValue(r.nombreChofer)}</td>
                    <td>{normalizeValue(r.numeroEmpleado)}</td>
                    <td><strong>{normalizeValue(r.numeroLicencia)}</strong></td>
                    <td>{normalizeValue(r.claseLicencia)}</td>
                    <td>{normalizeValue(r.venceLicencia)}</td>
                    <td>{normalizeValue(r.observaciones)}</td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div
                        onClick={() => !loading && handleToggleNotificaciones(r)}
                        style={{
                          width: '38px',
                          height: '20px',
                          backgroundColor: r.recibeNotificaciones ? '#10b981' : '#cbd5e1',
                          borderRadius: '12px',
                          position: 'relative',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s',
                          flexShrink: 0,
                          opacity: loading ? 0.6 : 1,
                          margin: '0 auto'
                        }}
                        title={r.recibeNotificaciones ? "Desactivar notificaciones" : "Activar notificaciones"}
                      >
                        <div style={{
                          width: '14px',
                          height: '14px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          position: 'absolute',
                          top: '3px',
                          left: r.recibeNotificaciones ? '21px' : '3px',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }} />
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8' }}>
                        {r.nombreEmpresa || 'Global'}
                      </span>
                    </td>

                    {/* 🔽 Botón EDITAR con ícono */}
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idChofer)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}

                    {/* 🔽 Botón BORRAR con ícono */}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const name = r.nombreChofer || r.numeroLicencia || `Chofer ${r.idChofer}`;
                            askDelete(r.idChofer, name);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={8 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            {modalMode === 'new' && (
              <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                <span>
                  Selecciona o crea un chofer y pulsa <b>Editar</b> para gestionar sus datos
                  relacionados.
                </span>
              </div>
            )}
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
            {modalMode === 'edit' && editingRowId > 0 && (
              <ChoferTabs 
                choferId={editingRowId} 
                puedeCrear={puedeCrear} 
                puedeEditar={puedeEditar} 
                puedeEliminar={puedeEliminar} 
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}
